import React, { Suspense, lazy } from 'react';
import GameBoard from './GameBoard';
import ScanAndFindItPromo from './ScanAndFindItPromo';
import {
  DEFAULT_DIFFICULTY,
  DIFFICULTIES,
  DIFFICULTY_ORDER,
} from '../sketches/difficultyConfig';
import '../App.css';

const MobileHome = lazy(() => import('./MobileHome'));

const SOLID_PATH =
  'M 8.00 28.20 Q 8.00 25.00 11.20 25.00 L 34.00 25.00 L 34.00 15.80 Q 34.00 13.00 36.12 14.83 L 54.18 30.43 Q 56.00 32.00 54.18 33.57 L 36.12 49.17 Q 34.00 51.00 34.00 48.20 L 34.00 39.00 L 11.20 39.00 Q 8.00 39.00 8.00 35.80 Z';

const PATH_CHANNEL =
  'M 8.00 28.20 Q 8.00 25.00 11.20 25.00 L 34.00 25.00 L 34.00 15.80 Q 34.00 13.00 36.12 14.83 L 54.18 30.43 Q 56.00 32.00 54.18 33.57 L 36.12 49.17 Q 34.00 51.00 34.00 48.20 L 34.00 39.00 L 11.20 39.00 Q 8.00 39.00 8.00 35.80 Z M 14.00 32.70 Q 14.00 34.50 15.80 34.50 L 31.00 34.50 L 31.00 41.20 Q 31.00 43.00 32.48 41.98 L 45.68 32.91 Q 47.00 32.00 45.68 31.09 L 32.48 22.02 Q 31.00 21.00 31.00 22.80 L 31.00 29.50 L 15.80 29.50 Q 14.00 29.50 14.00 31.30 Z';

const ALIGNED_BAR =
  'M 8.00 28.20 Q 8.00 25.00 11.20 25.00 L 26.50 25.00 Q 28.00 25.00 28.00 26.50 L 28.00 37.50 Q 28.00 39.00 26.50 39.00 L 11.20 39.00 Q 8.00 39.00 8.00 35.80 Z';

const ALIGNED_CHEVRON =
  'M 32.00 15.50 Q 32.00 13.00 33.96 14.55 L 54.12 30.51 Q 56.00 32.00 54.12 33.49 L 33.96 49.45 Q 32.00 51.00 32.00 48.50 L 32.00 43.20 Q 32.00 42.00 32.98 41.30 L 44.78 32.87 Q 46.00 32.00 44.78 31.13 L 32.98 22.70 Q 32.00 22.00 32.00 20.80 Z';

function GuidanceSymbols() {
  return (
    <div className="arrow-cont guidance-beacon" aria-hidden="true">
      <svg
        className="arrow guidance-chase guidance-chase--1"
        viewBox="0 0 64 64"
        focusable="false"
      >
        <path fill="currentColor" d={SOLID_PATH} />
      </svg>
      <svg
        className="arrow guidance-chase guidance-chase--2"
        viewBox="0 0 64 64"
        focusable="false"
      >
        <path fill="currentColor" fillRule="evenodd" d={PATH_CHANNEL} />
      </svg>
      <svg
        className="arrow guidance-chase guidance-chase--3"
        viewBox="0 0 64 64"
        focusable="false"
      >
        <path fill="currentColor" d={ALIGNED_BAR} />
        <path fill="currentColor" d={ALIGNED_CHEVRON} />
      </svg>
    </div>
  );
}

class Home extends React.Component {
  constructor() {
    super();
    this.state = {
      nextPage: null,
      home: false,
      isMobile: window.innerWidth <= 1024,
      difficulty: DEFAULT_DIFFICULTY,
    };
    this.handleDifficultyChange = (id) => {
      if (!DIFFICULTIES[id]) return;
      if (id === this.state.difficulty) return;
      this.setState({ difficulty: id });
    };
    this.mediaQuery = window.matchMedia('(max-width: 1024px)');
    this.handleViewportChange = () => {
      this.setState({ isMobile: this.mediaQuery.matches });
    };
  }
  componentDidMount() {
    window.scrollTo(0, 0);
    this.setState({ nextPage: '/aboutMe', home: true });
    this.mediaQuery.addListener(this.handleViewportChange);
  }
  componentWillUnmount() {
    this.mediaQuery.removeListener(this.handleViewportChange);
  }
  render() {
    // Mobile uses MobileHome only — avoids a hidden desktop H1 still in the DOM.
    if (this.state.isMobile) {
      return (
        <Suspense fallback={null}>
          <MobileHome />
        </Suspense>
      );
    }

    const difficulty = this.state.difficulty || DEFAULT_DIFFICULTY;
    const stageIndex = Math.max(0, DIFFICULTY_ORDER.indexOf(difficulty));

    return (
      <div className="Home">
        <div className="home-title-banner instructions">
          <h1 className="home-text">Wesam Technologies</h1>
        </div>
        <div className={'home-playfield home-playfield--' + difficulty}>
          <div className="home-playfield__ui">
            <div
              className="adventure-progress"
              role="status"
              aria-label={
                'Adventure stage: ' +
                DIFFICULTIES[difficulty].label +
                ' — ' +
                DIFFICULTIES[difficulty].personality
              }
            >
              {DIFFICULTY_ORDER.map((id, index) => {
                const isCurrent = id === difficulty;
                const isDone = index < stageIndex;
                return (
                  <React.Fragment key={id}>
                    {index > 0 ? (
                      <span
                        className={
                          'adventure-progress__connector' +
                          (index <= stageIndex
                            ? ' adventure-progress__connector--lit'
                            : '')
                        }
                        aria-hidden="true"
                      />
                    ) : null}
                    <div
                      className={
                        'adventure-progress__stage adventure-progress__stage--' +
                        id +
                        (isCurrent
                          ? ' adventure-progress__stage--current'
                          : '') +
                        (isDone ? ' adventure-progress__stage--done' : '')
                      }
                    >
                      <span className="adventure-progress__label">
                        {DIFFICULTIES[id].label}
                      </span>
                      <span className="adventure-progress__personality">
                        {DIFFICULTIES[id].personality}
                      </span>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          {/* Clipped game frame — canvas cannot draw outside these borders */}
          <div className="home-game-frame">
            <div className="home-game-frame__arrows" aria-hidden="true">
              <GuidanceSymbols />
            </div>
            <GameBoard
              contained
              nextPage={this.state.nextPage}
              home={this.state.home}
              difficulty={difficulty}
              onDifficultyChange={this.handleDifficultyChange}
            />
          </div>
          <ScanAndFindItPromo />
        </div>
      </div>
    );
  }
}

export default Home;
